import { createApi } from '@reduxjs/toolkit/query/react';
import { AppDispatch } from 'store';
import { v4 as uuid } from 'uuid';

import { apiSahBudgets } from '~constants/api';

import { Paginated, PaginatedRequest, Request, rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

import { Supplement } from '~lookups/models';
import { Budget, BudgetActivitiesLog, Section, SectionItem } from '~support-at-home/models';

export const typePrefix = `clients-budgets`;

export type BudgetReq = Request & { id: string };

export type SectionReq = {
  revision_id?: string;
  client_id: number | undefined;
  package_fixed_id: number | undefined;
  start_date?: string;
  end_date?: string;
  ignore_sah_filters?: boolean;
};

interface BulkCopyEstimateRequest {
  start_date: string;
  end_date: string;
  client_ids?: number[];
  funding_type_ids?: number[];
  status_ids?: string[];
}

interface BulkCopyRequest {
  budget_ids: string[];
  start_date: string;
  end_date: string;
}

interface BulkSahPreviewRequest {
  budget_ids: string[];
}

export interface SahCandidate {
  id: string;
  label: string;
}

export interface BudgetSahPreview {
  budget_id: string;
  reference: string;
  client_name: string;
  start_date: string;
  end_date: string;
  funding_type_name: string;
  current_sah_budget_id: string | null;
  candidates: SahCandidate[];
}

export interface SahAssignment {
  budget_id: string;
  sah_budget_id: string | null;
}

interface BulkSahAssignRequest {
  assignments: SahAssignment[];
}

interface BulkSahAssignResult {
  assigned_count: number;
}

export interface CopyServicesReq {
  id: string;
  revision_id: string;
  client_id: number;
  package_fixed_id: number;
  start_date: string;
  end_date: string;
}

export const budgetsApi = createApi({
  reducerPath: 'budgetsApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiSahBudgets }),
  tagTypes: ['budgets', 'services', 'contributions', 'logs'],
  endpoints: (builder) => ({
    getBudgets: builder.query<Budget[], Request | void>({
      query: (params) => ({ url: '', method: 'GET', params }),
      providesTags: [{ type: 'budgets', id: 'LIST' }],
      transformResponse: (payload) => unwrap<Budget[]>(payload),
    }),

    getBudgetsPaginated: builder.query<Paginated<Budget>, PaginatedRequest>({
      query: (params) => ({ url: '', method: 'GET', params }),
      providesTags: [{ type: 'budgets', id: 'LIST' }],
      transformResponse: (payload) => unwrap<Paginated<Budget>>(payload),
    }),

    getBudget: builder.query<Budget, BudgetReq>({
      query: ({ id, ...rest }) => ({ url: `/${id}`, method: 'GET', params: rest }),
      providesTags: (_result, _error, { id }) => [{ type: 'budgets', id }],
      transformResponse: (payload) => unwrap<Budget>(payload),
    }),

    getBulkCopyEstimate: builder.query<Budget[], BulkCopyEstimateRequest>({
      query: (params) => ({ url: '/bulk-copy-estimate', method: 'GET', params }),
      transformResponse: (payload) => unwrap<Budget[]>(payload),
    }),

    saveBudget: builder.mutation<Budget, Partial<Budget>>({
      query: (body) => ({ url: '', method: 'POST', body }),
      invalidatesTags: (result) =>
        result?.id
          ? [
              { type: 'budgets', id: 'LIST' },
              { type: 'budgets', id: result.id },
            ]
          : [{ type: 'budgets', id: 'LIST' }],
      transformResponse: (payload) => unwrap<Budget>(payload),
    }),

    deleteBudget: builder.mutation({
      query: (id) => ({ url: `/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'budgets', id: 'LIST' },
        { type: 'budgets', id },
      ],
      transformResponse: (payload) => unwrap<any>(payload),
    }),

    bulkUpdate: builder.mutation<any, any>({
      query: (body) => ({ url: `/bulk-update`, method: 'POST', body }),
      invalidatesTags: (_result) => [{ type: 'budgets', id: 'LIST' }],
      transformResponse: (payload) => unwrap<any>(payload),
    }),

    bulkCopy: builder.mutation<Budget[], BulkCopyRequest>({
      query: (body) => ({ url: `/bulk-copy`, method: 'POST', body }),
      invalidatesTags: (_result) => [{ type: 'budgets', id: 'LIST' }],
      transformResponse: (payload) => unwrap<Budget[]>(payload),
    }),

    bulkSahPreview: builder.mutation<BudgetSahPreview[], BulkSahPreviewRequest>({
      query: (body) => ({ url: `/bulk-sah-preview`, method: 'POST', body }),
      transformResponse: (payload) => unwrap<BudgetSahPreview[]>(payload),
    }),

    bulkSahAssign: builder.mutation<BulkSahAssignResult, BulkSahAssignRequest>({
      query: (body) => ({ url: `/bulk-sah-assign`, method: 'POST', body }),
      invalidatesTags: [{ type: 'budgets', id: 'LIST' }],
      transformResponse: (payload) => unwrap<BulkSahAssignResult>(payload),
    }),

    recalculate: builder.mutation<any, any>({
      query: (body) => ({ url: `/recalculate`, method: 'POST', body }),
      transformResponse: (payload) => unwrap<Budget>(payload),
    }),

    getPrefilledServices: builder.query<any, Partial<SectionReq>>({
      query: (params) => ({ url: `/services`, method: 'GET', params }),
      providesTags: [{ type: 'services', id: 'LIST' }],
      transformResponse: (payload) => unwrap<any>(payload),
    }),

    getFilteredServices: builder.query<any, Partial<SectionReq>>({
      query: (params) => ({ url: `/filtered-services`, method: 'GET', params }),
      providesTags: (_result, _error, args) => [
        {
          type: 'services',
          id: `${args.ignore_sah_filters ?? false}-${args.revision_id ?? ''}-${args.client_id ?? ''}-${args.package_fixed_id ?? ''}`,
        },
      ],
      transformResponse: (payload) => unwrap<any>(payload),
    }),

    getContributions: builder.query<any, Partial<SectionReq>>({
      query: (params) => ({ url: `/contributions`, method: 'GET', params }),
      providesTags: [{ type: 'contributions', id: 'LIST' }],
      transformResponse: (payload) => unwrap<any>(payload),
    }),

    getLogs: builder.query<BudgetActivitiesLog[], BudgetReq>({
      query: ({ id, ...rest }) => ({ url: `/${id}/logs`, method: 'GET', params: rest }),
      providesTags: [{ type: 'logs', id: 'LIST' }],
      transformResponse: (payload) => unwrap<BudgetActivitiesLog[]>(payload),
    }),

    getCopyServices: builder.query<{ sections: Section[]; supplements: Supplement[] }, CopyServicesReq>({
      query: ({ id, ...params }) => ({ url: `/${id}/copy`, method: 'GET', params }),
      transformResponse: (payload) => unwrap<{ sections: Section[]; supplements: Supplement[] }>(payload),
    }),

    updateRevision: builder.mutation<Budget, { id: string }>({
      query: ({ id }) => ({ url: `/${id}/update-revision`, method: 'POST' }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'budgets', id: 'LIST' },
        { type: 'budgets', id },
      ],
      transformResponse: (payload) => unwrap<Budget>(payload),
    }),
  }),
});

// AsyncThunk for orchestrating multiple requests to build sections data
export const buildSections =
  ({ revision_id, client_id, package_fixed_id, start_date, end_date }: Partial<SectionReq>) =>
  async (dispatch: AppDispatch) => {
    if (!package_fixed_id) {
      console.warn('buildSections skipped: missing package_fixed_id');
      return [];
    }

    try {
      // Run both service + contribution requests in parallel
      const [prefilled, contributions] = await Promise.all([
        dispatch(
          budgetsApi.endpoints.getPrefilledServices.initiate({
            revision_id,
            client_id,
            package_fixed_id,
          }),
        ).unwrap(),
        start_date && end_date
          ? dispatch(
              budgetsApi.endpoints.getContributions.initiate({
                client_id,
                package_fixed_id,
                start_date,
                end_date,
              }),
            ).unwrap()
          : Promise.resolve([]),
      ]);

      // Merge the data together
      const sections = prefilled.map((category: any) => {
        const contrib = contributions.find((c: any) => c.category_id === category.id);
        return {
          id: uuid(),
          category_id: category.id,
          category,
          contrib_proportion: contrib ? contrib.contrib_proportion : null,
          items: category.services.map((service: any) => ({
            id: uuid(),
            category_id: category.id,
            service_type_id: service.id,
            start_date,
            end_date,
            week_days: [],
            include_public_holidays: false,
            is_external_provider: false,
          })),
          totals: { cost: 0, contribution: 0, govSubsidy: 0 },
        };
      });
      return sections;
    } catch (error) {
      console.error('buildSections failed:', error);
      return [];
    }
  };

// Thunk for building copy sections — fetches reconciled data from API, merges with contributions
export const buildCopySections =
  ({ id, revision_id, client_id, package_fixed_id, start_date, end_date }: CopyServicesReq) =>
  async (dispatch: AppDispatch) => {
    try {
      const [copyData, contributions] = await Promise.all([
        dispatch(
          budgetsApi.endpoints.getCopyServices.initiate({
            id,
            revision_id,
            client_id,
            package_fixed_id,
            start_date,
            end_date,
          }),
        ).unwrap(),
        start_date && end_date
          ? dispatch(
              budgetsApi.endpoints.getContributions.initiate({
                client_id,
                package_fixed_id,
                start_date,
                end_date,
              }),
            ).unwrap()
          : Promise.resolve([]),
      ]);

      const sections = (copyData.sections ?? []).map((section: Section) => {
        const contrib = contributions.find((c: { category_id: string }) => c.category_id === section.category_id);
        return {
          ...section,
          id: uuid(),
          contrib_proportion: contrib ? contrib.contrib_proportion : (section.contrib_proportion ?? null),
          items: (section.items ?? []).map((item: SectionItem) => ({
            ...item,
            id: uuid(),
          })),
        };
      });

      return { sections, supplements: copyData.supplements ?? [] };
    } catch (error) {
      console.error('buildCopySections failed:', error);
      throw error;
    }
  };

export const {
  useGetBudgetsQuery,
  useLazyGetBudgetsQuery,
  useGetBudgetsPaginatedQuery,
  useGetBudgetQuery,
  useGetBulkCopyEstimateQuery,
  useLazyGetBudgetQuery,
  useSaveBudgetMutation,
  useDeleteBudgetMutation,
  useBulkUpdateMutation,
  useBulkCopyMutation,
  useRecalculateMutation,
  useGetPrefilledServicesQuery,
  useGetFilteredServicesQuery,
  useGetContributionsQuery,
  useGetLogsQuery,
  useUpdateRevisionMutation,
  useBulkSahPreviewMutation,
  useBulkSahAssignMutation,
} = budgetsApi;
